import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import bgtrace from "../assets/bgtrace1.jpeg";

const testimonials = [
  {
    name: "Ron de Leon",
    role: "Tacoma Owner",
    vehicle: "Front Camera System Install",
    text: "Taco Garage came through! Brought my own front camera system for Taco Garage to install on my Tacoma. The quality is outstanding!",
    rating: 5,
  },
  {
    name: "Angela Johnson",
    role: "Local Guide",
    vehicle: "DMM Install",
    text: "Easy install of the DMM. Quick shipping, but it was missing one piece I had added on but Taco Garage quickly responded and sent me what I needed. Quality product and customer service!",
    rating: 5,
  },
  {
    name: "Jervis Swannack",
    role: "Local Guide",
    vehicle: "Ecoflow Alternator Charger",
    text: "Andy did a great job installing an Ecoflow alternator charger. Very professional installation, took the time to make it look OEM. I highly recommend Taco Garage. They go the extra distance and exceed expectations.",
    rating: 5,
  },
  {
    name: "Junar Balanza",
    role: "Local Guide",
    vehicle: "Truck Modifications",
    text: "Very informative staff. The technician Andy is the best. All of my truck modifications have been done there and they always do their best to make sure the customer is comfortable.",
    rating: 5,
  },
  {
    name: "Hudson Elliott",
    role: "Tacoma Owner",
    vehicle: "2019 Toyota Tacoma",
    text: "DMM has been to hell and back in my 2019 Tacoma... thousands of off-road miles and still working perfectly! It’s a great product that every Tacoma needs!",
    rating: 5,
  },
];

const stats = [
  { number: "50,000+", label: "Happy Customers" },
  { number: "4.9/5", label: "Average Rating" },
  { number: "10,000+", label: "Products Sold" },
  { number: "24/7", label: "Support Available" },
];

const Testimonials = () => {
  const [current, setCurrent] = useState(0);
  const next = () => setCurrent((prev) => (prev + 1) % testimonials.length);
  const prev = () => setCurrent((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  return (
    <section 
      className="relative py-20 text-black border-t border-gray-200 overflow-hidden md:bg-none"
      style={{
        backgroundImage: `url(${bgtrace})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* 🌎 Mobile-only white overlay for readability */}
      <div className="absolute inset-0 bg-white/80 md:bg-transparent pointer-events-none z-0"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* 🌫 Dark Glass Box for Title Section */}
        <div className="bg-gray-900/90 text-white backdrop-blur-md rounded-2xl shadow-lg p-8 md:p-12 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4 drop-shadow">
              What Our <span className="text-brandred">Customers</span> Say
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Real reviews from our community of Toyota enthusiasts.
            </p>

            <div className="mt-3 flex justify-center items-center gap-2 text-sm text-gray-400">
              <div className="flex items-center">
                <span className="text-xl font-semibold text-white">4.8</span>
                <span className="text-yellow-400 ml-1 flex">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4 fill-yellow-400"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 17.27L18.18 21 16.54 13.97 22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                    </svg>
                  ))}
                </span>
              </div>
              <span className="ml-2">264+ Google Reviews</span>
            </div>
          </motion.div>

          {/* 📊 Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {stats.map((stat, i) => (
              <div
                key={i}
                className="text-center bg-black/50 rounded-xl py-6 px-4 border border-gray-800 shadow-inner"
              >
                <div className="text-3xl md:text-4xl font-bold text-brandred mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-300 text-sm md:text-base">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* 💬 Testimonial Carousel */}
        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <div className="bg-gray-900/95 text-white rounded-2xl p-6 md:p-8 border border-gray-700 shadow-lg max-w-2xl mx-auto backdrop-blur-md">
                <div className="flex justify-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={20}
                      className={`${
                        i < testimonials[current].rating
                          ? "text-yellow-400 fill-current"
                          : "text-gray-500"
                      }`}
                    />
                  ))}
                </div>
                <blockquote className="text-lg md:text-xl italic mb-4 text-gray-200">
                  "{testimonials[current].text}"
                </blockquote>
                <div className="font-semibold">{testimonials[current].name}</div>
                <div className="text-sm text-gray-400">{testimonials[current].role}</div>
                <div className="text-xs text-brandred font-medium mt-1">
                  {testimonials[current].vehicle}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <button
            onClick={prev}
            className="absolute left-0 top-1/2 -translate-y-1/2 w-9 h-9 bg-brandred text-white rounded-full flex items-center justify-center hover:bg-brandred/90 shadow-lg transition-all duration-300"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={next}
            className="absolute right-0 top-1/2 -translate-y-1/2 w-9 h-9 bg-brandred text-white rounded-full flex items-center justify-center hover:bg-brandred/90 shadow-lg transition-all duration-300"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Pagination Dots */}
        <div className="flex justify-center mt-6 gap-2">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                i === current
                  ? "bg-brandred scale-125"
                  : "bg-gray-500 hover:bg-gray-400"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
